import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiFout, api } from '@/lib/api';
import type { AppToken, AuthStatus, PraktijkVerbruik } from '@/lib/api-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Settings, LogOut, Download, FileCode, BookOpen, Copy, Key, Brain, Building2, Palette, Receipt, List, Mail, Plus, ToggleLeft, ToggleRight, Lock, Unlock, FileDown, TrendingUp, UserCog, FileText, AlertTriangle, MessageSquare, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { ErrorBoundary } from '@/components/ErrorBoundary';

import { PracticesTab } from '@/components/settings/PracticesTab';
import { BillingSettingsTab } from '@/components/settings/BillingSettingsTab';
import { DisplaySettingsTab } from '@/components/settings/DisplaySettingsTab';
import { NoticeTab } from '@/components/settings/NoticeTab';
import { AiSettingsCard } from '@/components/settings/AiSettingsCard';
import { UsageLogsTab } from '@/components/settings/UsageLogsTab';
import { ErrorLogsTab } from '@/components/settings/ErrorLogsTab';
import { FeedbackTab } from '@/components/settings/FeedbackTab';
import { EmailTemplatesTab } from '@/components/settings/EmailTemplatesTab';
import { FinancialResultTab } from '@/components/settings/FinancialResultTab';
import { AdminUsersTab } from '@/components/settings/AdminUsersTab';

const SECTIONS = [
  { id: 'token', label: 'App Tokens', icon: Key },
  { id: 'admin-users', label: 'Admin Users', icon: UserCog },
  { id: 'ai', label: 'AI Instellingen', icon: Brain },
  { id: 'practices', label: 'Praktijken', icon: Building2 },
  { id: 'display', label: 'Weergave', icon: Palette },
  { id: 'notice', label: 'Melding', icon: Megaphone },
  { id: 'billing', label: 'Facturatie', icon: Receipt },
  { id: 'financial', label: 'Financieel Resultaat', icon: TrendingUp },
  { id: 'usage-logs', label: 'Verbruikslog', icon: List },
  { id: 'error-logs', label: 'Foutenlog', icon: AlertTriangle },
  { id: 'feedback', label: 'SOEP-feedback', icon: MessageSquare },
  { id: 'email-templates', label: 'Email Templates', icon: Mail },
  { id: 'export', label: 'Kosten Export', icon: Download },
  { id: 'soep-return', label: 'SOEP Return', icon: FileCode },
  { id: 'his-manual', label: 'HIS Handleiding', icon: FileDown },
  { id: 'dpa-template', label: 'Verwerkersovereenkomst', icon: FileText },
  { id: 'docs', label: 'Documentatie', icon: BookOpen },
] as const;

type SectionId = (typeof SECTIONS)[number]['id'];

/**
 * app_tokens.app_token is een uuid-kolom, en de tokenruil weigert alles wat er niet
 * op lijkt. Een zelf opgegeven token moet dus dit formaat hebben; de API controleert
 * het ook, dit is alleen om er niet voor naar de server te hoeven.
 */
const UUID_PATROON = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Het verbruiksoverzicht per praktijk, met de maandregels erin. GET /api/admin/usage
 * levert die vorm; PraktijkVerbruik in api-types beschrijft de samengevatte variant
 * die de andere schermen gebruiken.
 */
interface PraktijkVerbruikMetMaanden {
  practiceId: string;
  practiceName: string;
  agbCode: string;
  afasId: string | null;
  months: Array<{ month: string; purchase: number; selling: number; entries: number }>;
  totals: { purchase: number; selling: number; entries: number };
}

export default function AdminSettingsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [appTokens, setAppTokens] = useState<AppToken[]>([]);
  const [isLoadingAppToken, setIsLoadingAppToken] = useState(true);
  const [newAppName, setNewAppName] = useState('');
  const [newAppToken, setNewAppToken] = useState('');
  const [isAddingToken, setIsAddingToken] = useState(false);

  const activeSection = (searchParams.get('tab') as SectionId) || 'token';

  const setActiveSection = (id: SectionId) => {
    setSearchParams({ tab: id });
  };

  /**
   * De aanmeldcontrole.
   *
   * Was een Supabase-sessie met een onAuthStateChange-abonnement dat de gebruiker
   * eruit gooide zodra het token verliep. Entra werkt anders: de sessie zit in een
   * cookie dat het platform beheert, en er is niets om op te abonneren. Wat er
   * blijft is één vraag bij het openen -- ben ik beheerder? -- en het antwoord van
   * de API op elke volgende aanroep.
   */
  const [afmeldUrl, setAfmeldUrl] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const status = await api.get<AuthStatus>('/auth/status');
        if (!status.isAdmin && !status.ontwikkelmodus) {
          navigate('/admin/login', { replace: true });
          return;
        }
        setAfmeldUrl(status.afmeldUrl ?? null);
      } catch {
        navigate('/admin/login', { replace: true });
        return;
      } finally {
        setIsCheckingAuth(false);
      }
    })();
  }, [navigate]);

  useEffect(() => {
    void loadAppTokens();
  }, []);

  const handleLogout = () => {
    // Afmelden gebeurt bij het platform: dat wist het Entra-cookie en beëindigt de
    // sessie bij de identiteitsprovider. Een navigatie binnen de SPA zou alleen de
    // pagina wisselen en de sessie laten staan.
    window.location.href = afmeldUrl ?? '/.auth/logout';
  };

  const handleExportAll = async () => {
    setIsExporting(true);
    try {
      // GET /api/admin/usage vereist een beheerder. get-practice-usage had geen
      // enkele controle: met all_practices:true gaf die de volledige klantenlijst
      // met inkoop- en verkoopprijs, opvraagbaar met de anon-key uit de bundel.
      const { practices } = await api.get<{ practices: PraktijkVerbruikMetMaanden[] }>(
        '/admin/usage',
      );

      const sep = ';';
      const header = [
        'AGBCode',
        'Praktijknaam',
        'AFAS ID',
        'InkoopExBtw',
        'VerkoopExBTW',
        'MaandPeriode',
        'JaarPeriode',
      ].join(sep);
      const rows: string[] = [];

      for (const p of practices) {
        for (const maand of [...p.months].sort((a, b) => a.month.localeCompare(b.month))) {
          const [year, m] = maand.month.split('-');
          rows.push(
            [
              p.agbCode || '',
              p.practiceName || '',
              p.afasId || '',
              maand.purchase.toFixed(4).replace('.', ','),
              maand.selling.toFixed(4).replace('.', ','),
              m,
              year,
            ].join(sep),
          );
        }
      }

      if (rows.length === 0) {
        toast.info('Geen kosten gevonden om te exporteren');
        return;
      }

      // De BOM is er zodat Excel het bestand als UTF-8 opent; zonder die byte
      // worden accenten in praktijknamen onleesbaar.
      const csv = '\uFEFF' + [header, ...rows].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kosten-omzet-alle-praktijken-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export gedownload');
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Export mislukt');
    } finally {
      setIsExporting(false);
    }
  };

  const loadAppTokens = async () => {
    setIsLoadingAppToken(true);
    try {
      const { tokens } = await api.get<{ tokens: AppToken[] }>('/admin/app-tokens');
      setAppTokens(tokens);
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Kon de app tokens niet laden');
    } finally {
      setIsLoadingAppToken(false);
    }
  };

  /** Ingevuld, maar geen uuid -- dan kleurt het veld en zegt de regel eronder waarom. */
  const tokenInvoerOngeldig = newAppToken.trim() !== '' && !UUID_PATROON.test(newAppToken.trim());

  /**
   * Aanmaken met een naam, en optioneel met een zelf gekozen token.
   *
   * Blijft het tokenveld leeg, dan genereert de database er een -- dat is het
   * gewone geval. Zelf toekennen is er voor een HIS dat het token al in zijn eigen
   * configuratie heeft staan en dat niet meer kan aanpassen. Het formaat wordt
   * hier alvast gecontroleerd, zodat een typefout niet als serverfout terugkomt;
   * de API doet dezelfde controle, want dit scherm is niet de enige weg erheen.
   */
  const handleAddToken = async () => {
    if (!newAppName.trim()) {
      toast.error('Vul een app naam in');
      return;
    }
    const eigenToken = newAppToken.trim();
    if (eigenToken && !UUID_PATROON.test(eigenToken)) {
      toast.error('Een zelf opgegeven token moet een uuid zijn');
      return;
    }
    setIsAddingToken(true);
    try {
      await api.post('/admin/app-tokens', {
        appName: newAppName.trim(),
        ...(eigenToken ? { appToken: eigenToken } : {}),
      });
      toast.success(eigenToken ? 'App token toegekend' : 'App token aangemaakt');
      setNewAppName('');
      setNewAppToken('');
      await loadAppTokens();
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Kon token niet aanmaken');
    } finally {
      setIsAddingToken(false);
    }
  };

  /**
   * Deactiveren, niet verwijderen.
   *
   * Het scherm had een verwijderknop, en die is er niet meer. Een app token is wat
   * het HIS gebruikt om een praktijk aan te melden; hem weghalen sluit elke praktijk
   * buiten die hem gebruikt, en dat is niet ongedaan te maken. De API biedt daarom
   * geen DELETE aan -- alleen deze schakelaar, en de lijst laat zien hoeveel
   * praktijken eraan hangen.
   */
  const handleToggleTokenActive = async (id: string, currentActive: boolean) => {
    try {
      await api.patch(`/admin/app-tokens/${id}`, { isActive: !currentActive });
      await loadAppTokens();
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Kon status niet wijzigen');
    }
  };

  /**
   * De oude manier van aanroepen af- of aanzetten voor deze leverancier.
   *
   * Uit betekent: alleen nog via POST /api/launch, dus geen app_token meer in de
   * URL van de browser. Dat is de laatste stap van een migratie en niet de eerste
   * -- zet hem pas om als de leverancier de launch daadwerkelijk gebruikt, want
   * daarna werkt zijn oude weg niet meer. Zie docs/his-launch-interface.md.
   */
  const handleToggleDirectSession = async (id: string, huidig: boolean) => {
    if (huidig && !window.confirm(
      'Hierna werkt de oude aanroep met tokens in de URL niet meer voor dit app-token. ' +
      'Alleen doen als deze leverancier POST /api/launch gebruikt. Doorgaan?',
    )) {
      return;
    }

    try {
      await api.patch(`/admin/app-tokens/${id}`, { allowDirectSession: !huidig });
      toast.success(huidig ? 'Alleen nog via de launch' : 'De oude aanroep is weer toegestaan');
      await loadAppTokens();
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Kon de instelling niet wijzigen');
    }
  };

  const copyTokenToClipboard = (token: string) => {
    navigator.clipboard.writeText(token);
    toast.success('App token gekopieerd');
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="border-b px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Admin
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-1" />
            Uitloggen
          </Button>
        </div>
      </div>

      {/* Body: sidebar + content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <nav className="w-48 shrink-0 border-r bg-muted/30 p-2 space-y-0.5 overflow-y-auto">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => {
                if (s.id === 'docs') {
                  navigate('/admin/docs');
                } else if (s.id === 'his-manual') {
                  const link = document.createElement('a');
                  link.href = '/iSOFT-HIS-Integratiehandleiding.pdf';
                  link.download = 'iSOFT-HIS-Integratiehandleiding.pdf';
                  link.click();
                  toast.success('HIS Integratiehandleiding wordt gedownload');
                } else if (s.id === 'dpa-template') {
                  // Het blanco exemplaar uit public/, met de plaatshouder voor de
                  // praktijknaam er nog in -- een beheerder heeft hier geen praktijk
                  // om in te vullen. De bijlage bij de verificatiemail en de
                  // download in het instellingenscherm van een arts komen uit
                  // hetzelfde bestand, maar krijgen de naam erin gezet; zie
                  // apps/api/src/lib/vwo-pdf.ts.
                  const link = document.createElement('a');
                  link.href = '/verwerkersovereenkomst-ai-diensten.pdf';
                  link.download = 'Verwerkersovereenkomst-AI-diensten-blanco.pdf';
                  link.click();
                  toast.success('Blanco verwerkersovereenkomst wordt gedownload');
                } else if (s.id === 'export') {
                  handleExportAll();
                } else {
                  setActiveSection(s.id);
                }
              }}
              className={cn(
                'w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors text-left',
                activeSection === s.id && s.id !== 'docs' && s.id !== 'export' && s.id !== 'his-manual' && s.id !== 'dpa-template'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <s.icon className="h-4 w-4 shrink-0" />
              {s.id === 'export' && isExporting ? (
                <><Loader2 className="h-3 w-3 animate-spin" /> Exporteren...</>
              ) : (
                s.label
              )}
            </button>
          ))}
        </nav>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/*
            Een fout in één tabblad hoort niet het hele beheerscherm zwart te maken.
            De key is de sectie: daarmee is een nieuw tabblad een nieuwe grens en
            blijft een opgevangen fout niet staan nadat je verder klikt.
          */}
          <ErrorBoundary
            key={activeSection}
            naam={SECTIONS.find((s) => s.id === activeSection)?.label}
            className="max-w-4xl mx-auto"
          >
            {activeSection === 'token' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="h-5 w-5" />
                    App Tokens
                  </CardTitle>
                  <CardDescription>
                    Beheer app tokens. Elke praktijk is gekoppeld aan één app token. Voeg <code className="bg-muted px-1 rounded text-xs">app_token=...</code> toe aan de URL.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isLoadingAppToken ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <div className="space-y-2">
                        {appTokens.map((t) => (
                          <div key={t.id} className={cn("flex items-center gap-2 rounded-md border p-3", !t.isActive && "opacity-50")}>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium">{t.appName || 'Naamloos'}</p>
                              <p className="text-xs font-mono text-muted-foreground truncate">{t.appToken}</p>
                              {typeof t.practiceCount === 'number' && (
                                <p className="text-xs text-muted-foreground">
                                  {t.practiceCount === 1
                                    ? '1 praktijk gebruikt dit token'
                                    : `${t.practiceCount} praktijken gebruiken dit token`}
                                </p>
                              )}
                              <p className="text-xs text-muted-foreground">
                                {t.allowDirectSession
                                  ? 'Oude aanroep (tokens in de URL) is toegestaan'
                                  : 'Alleen via POST /api/launch'}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 shrink-0"
                              onClick={() => handleToggleDirectSession(t.id, t.allowDirectSession)}
                              title={t.allowDirectSession ? 'Oude aanroep uitzetten' : 'Oude aanroep weer toestaan'}
                            >
                              {t.allowDirectSession ? <Unlock className="h-3.5 w-3.5 text-amber-600" /> : <Lock className="h-3.5 w-3.5 text-green-600" />}
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => copyTokenToClipboard(t.appToken)} title="Kopieer">
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => handleToggleTokenActive(t.id, t.isActive)} title={t.isActive ? 'Deactiveren' : 'Activeren'}>
                              {t.isActive ? <ToggleRight className="h-3.5 w-3.5 text-green-600" /> : <ToggleLeft className="h-3.5 w-3.5" />}
                            </Button>
                          </div>
                        ))}
                        {appTokens.length === 0 && (
                          <p className="text-sm text-muted-foreground">Geen app tokens aangemaakt.</p>
                        )}
                      </div>
                      <div className="space-y-2 pt-2 border-t">
                        <div className="flex items-center gap-2">
                          <Input
                            value={newAppName}
                            onChange={(e) => setNewAppName(e.target.value)}
                            placeholder="Naam voor nieuwe app token..."
                            className="text-sm"
                            onKeyDown={(e) => e.key === 'Enter' && handleAddToken()}
                          />
                          <Input
                            value={newAppToken}
                            onChange={(e) => setNewAppToken(e.target.value)}
                            placeholder="Token (leeg = zelf genereren)"
                            className={cn('text-sm font-mono', tokenInvoerOngeldig && 'border-destructive')}
                            spellCheck={false}
                            autoComplete="off"
                            aria-label="Zelf op te geven app token"
                            aria-invalid={tokenInvoerOngeldig}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddToken()}
                          />
                          <Button variant="outline" size="sm" onClick={handleAddToken} disabled={isAddingToken} className="shrink-0">
                            {isAddingToken ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" />Toevoegen</>}
                          </Button>
                        </div>
                        <p className={cn('text-xs', tokenInvoerOngeldig ? 'text-destructive' : 'text-muted-foreground')}>
                          {newAppToken.trim() === ''
                            ? 'Laat het tokenveld leeg om een token te laten genereren.'
                            : tokenInvoerOngeldig
                              ? 'Een zelf opgegeven token moet een uuid zijn, bijvoorbeeld 3f2b1c94-7a5e-4d61-9b02-8e4c1d7a6f30.'
                              : 'Dit token wordt toegekend in plaats van een gegenereerd token.'}
                        </p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {activeSection === 'ai' && <AiSettingsCard />}
            {activeSection === 'practices' && <PracticesTab />}
            {activeSection === 'display' && <DisplaySettingsTab />}
            {activeSection === 'notice' && <NoticeTab />}
            {activeSection === 'billing' && <BillingSettingsTab />}
            {activeSection === 'financial' && <FinancialResultTab />}
            {activeSection === 'usage-logs' && <UsageLogsTab />}
            {activeSection === 'error-logs' && <ErrorLogsTab />}
            {activeSection === 'feedback' && <FeedbackTab />}
            {activeSection === 'email-templates' && <EmailTemplatesTab />}
            {activeSection === 'admin-users' && <AdminUsersTab />}
            {activeSection === 'soep-return' && <SoepReturnContent />}
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
}

/* SOEP Return content rendered inline */
function SoepReturnContent() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileCode className="h-5 w-5" />
          SOEP Return — Technische Beschrijving
        </CardTitle>
        <CardDescription>Hoe de gegenereerde SOEP-data wordt teruggestuurd naar MicroHIS</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <section>
          <h3 className="font-semibold text-base mb-1">Overzicht</h3>
          <p className="text-muted-foreground">
            Na het genereren van een SOEP-notitie wordt de data automatisch teruggestuurd naar de Delphi-host (MicroHIS).
            Het systeem gebruikt twee methoden, waarbij WebBridge prioriteit heeft.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-base mb-1">Enkel / Multi SOEP modus</h3>
          <p className="text-muted-foreground mb-2">
            Bovenin het opnamescherm staat een <strong>Enkel/Multi</strong> toggle knop. Hiermee bepaalt de zorgverlener vóór het genereren of de AI één of meerdere deelcontacten mag aanmaken:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
            <li><strong>Multi</strong> (standaard) — De AI maakt een apart SOEP-verslag per besproken probleem.</li>
            <li><strong>Enkel</strong> — De AI combineert alles in één enkel SOEP-verslag, ongeacht het aantal besproken klachten.</li>
          </ul>
          <p className="text-muted-foreground mt-2">
            In beide modi bevat de payload altijd een <code className="bg-muted px-1 rounded text-xs">problems</code> array — bij "Enkel" bevat deze precies 1 item.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-base mb-1">1. WebBridge (primair — WebView2)</h3>
          <p className="text-muted-foreground mb-2">
            Wanneer de app draait in een WebView2-context (embedded in MicroHIS), wordt <code className="bg-muted px-1 rounded text-xs">window.webBridge</code> gebruikt
            om data direct naar Delphi te sturen via <code className="bg-muted px-1 rounded text-xs">chrome.webview.postMessage()</code>.
          </p>
          <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto whitespace-pre-wrap font-mono">
{`// Detectie
const wb = window.webBridge;
if (wb && wb.isAvailable()) {
  wb.emit('soep', payload);
}

// Bericht dat Delphi ontvangt:
{
  "type": "event",
  "name": "soep",
  "data": {
    "consultation_date": "2026-03-01T10:30:00.000Z",
    "problems": [
      {
        "title": "Hoofdpijn",
        "subjective": "Patiënt klaagt over...",
        "objective": "Bij onderzoek...",
        "evaluation": "Vermoedelijk...",
        "plan": "Beleid: ...",
        "icpc_code": "N01",
        "icpc_description": "Hoofdpijn"
      }
    ],
    "subjective": "Patiënt klaagt over...",
    "objective": "Bij onderzoek...",
    "evaluation": "Vermoedelijk...",
    "plan": "Beleid: ...",
    "transcript": "Origineel transcript..."
  }
}`}
          </pre>
        </section>

        <section>
          <h3 className="font-semibold text-base mb-1">2. HTTP POST (fallback)</h3>
          <p className="text-muted-foreground mb-2">
            Als WebBridge niet beschikbaar is, wordt een HTTP POST gestuurd naar de
            geconfigureerde <code className="bg-muted px-1 rounded text-xs">local_api_url</code> (standaard: <code className="bg-muted px-1 rounded text-xs">http://localhost:8080/soep</code>).
          </p>
          <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto whitespace-pre-wrap font-mono">
{`POST http://localhost:8080/soep
Content-Type: application/json

{
  "consultation_date": "2026-03-01T10:30:00.000Z",
  "problems": [ ... ],
  "subjective": "...",
  "objective": "...",
  "evaluation": "...",
  "plan": "...",
  "transcript": "..."
}`}
          </pre>
        </section>

        <section>
          <h3 className="font-semibold text-base mb-1">ICPC-codes</h3>
          <p className="text-muted-foreground mb-2">
            Na het genereren wordt een reviewscherm getoond. Per deelcontact worden ICPC-suggesties opgehaald.
            De arts kan een suggestie selecteren of handmatig zoeken. De gekozen code wordt meegestuurd in <code className="bg-muted px-1 rounded text-xs">icpc_code</code> en <code className="bg-muted px-1 rounded text-xs">icpc_description</code>.
          </p>
        </section>

        <section>
          <h3 className="font-semibold text-base mb-1">Payload Velden</h3>
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted">
                  <th className="text-left p-2 font-medium">Veld</th>
                  <th className="text-left p-2 font-medium">Type</th>
                  <th className="text-left p-2 font-medium">Beschrijving</th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                <tr className="border-t"><td className="p-2 font-mono">consultation_date</td><td className="p-2">string (ISO 8601)</td><td className="p-2">Datum/tijd van het consult</td></tr>
                <tr className="border-t"><td className="p-2 font-mono">problems</td><td className="p-2">array</td><td className="p-2">Array van SOEP-verslagen</td></tr>
                <tr className="border-t"><td className="p-2 font-mono">problems[].title</td><td className="p-2">string | null</td><td className="p-2">Korte beschrijving</td></tr>
                <tr className="border-t"><td className="p-2 font-mono">problems[].subjective</td><td className="p-2">string | null</td><td className="p-2">S — Subjectief</td></tr>
                <tr className="border-t"><td className="p-2 font-mono">problems[].objective</td><td className="p-2">string | null</td><td className="p-2">O — Objectief</td></tr>
                <tr className="border-t"><td className="p-2 font-mono">problems[].evaluation</td><td className="p-2">string | null</td><td className="p-2">E — Evaluatie</td></tr>
                <tr className="border-t"><td className="p-2 font-mono">problems[].plan</td><td className="p-2">string | null</td><td className="p-2">P — Plan</td></tr>
                <tr className="border-t"><td className="p-2 font-mono">problems[].icpc_code</td><td className="p-2">string | null</td><td className="p-2">ICPC-code</td></tr>
                <tr className="border-t"><td className="p-2 font-mono">problems[].icpc_description</td><td className="p-2">string | null</td><td className="p-2">ICPC omschrijving</td></tr>
                <tr className="border-t bg-muted/50"><td className="p-2 font-mono">subjective</td><td className="p-2">string | null</td><td className="p-2"><em>Legacy</em></td></tr>
                <tr className="border-t bg-muted/50"><td className="p-2 font-mono">objective</td><td className="p-2">string | null</td><td className="p-2"><em>Legacy</em></td></tr>
                <tr className="border-t bg-muted/50"><td className="p-2 font-mono">evaluation</td><td className="p-2">string | null</td><td className="p-2"><em>Legacy</em></td></tr>
                <tr className="border-t bg-muted/50"><td className="p-2 font-mono">plan</td><td className="p-2">string | null</td><td className="p-2"><em>Legacy</em></td></tr>
                <tr className="border-t"><td className="p-2 font-mono">transcript</td><td className="p-2">string | null</td><td className="p-2">Origineel transcript</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h3 className="font-semibold text-base mb-1">Delphi Integratie (WebView2)</h3>
          <p className="text-muted-foreground mb-2">
            In de Delphi-applicatie luister je naar WebView2 messages en itereer over de problems array:
          </p>
          <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto whitespace-pre-wrap font-mono">
{`// Delphi (pseudo-code)
procedure TForm.WebViewMessageReceived(...);
var
  Msg, Data, Problem: TJSONObject;
  Problems: TJSONArray;
begin
  Msg := TJSONObject.ParseJSONValue(...);
  if Msg.GetValue<string>('name') = 'soep' then
  begin
    Data := Msg.GetValue<TJSONObject>('data');
    Problems := Data.GetValue<TJSONArray>('problems');
    for i := 0 to Problems.Count - 1 do
    begin
      Problem := Problems.Items[i] as TJSONObject;
      CreateSOEPRecord(
        Problem.GetValue<string>('title'),
        Problem.GetValue<string>('subjective'),
        Problem.GetValue<string>('objective'),
        Problem.GetValue<string>('evaluation'),
        Problem.GetValue<string>('plan'),
        Problem.GetValue<string>('icpc_code'),
        Problem.GetValue<string>('icpc_description')
      );
    end;
  end;
end;`}
          </pre>
        </section>

        <section>
          <h3 className="font-semibold text-base mb-1">Flow Diagram</h3>
          <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto whitespace-pre font-mono">
{`┌─────────────┐  Enkel/Multi  ┌──────────────┐  AI SOEP     ┌──────────────┐
│  Opname-     │  toggle       │  transcribe  │  generatie   │  Review      │
│  scherm      │ ────────────► │  -to-soep    │ ───────────► │  scherm      │
└─────────────┘               └──────────────┘              └──────┬───────┘
                                                                   │ ICPC + edit
                                                                   ▼
┌─────────────┐  emit('soep')  ┌──────────────┐              ┌──────────────┐
│  MicroHIS   │ ◄──────────── │  WebBridge   │ ◄──────────── │  Verstuur    │
│  Per probleem│  postMessage  │  (JS layer)  │              │  payload     │
│  SOEP-record │               └──────────────┘              └──────────────┘
└─────────────┘`}
          </pre>
        </section>
      </CardContent>
    </Card>
  );
}
